import { useCallback } from 'react'
import { useSetRecoilState } from 'recoil'
import type { ApplicationType } from '@messenger/types'
import type { EditorSaveVersionReason } from '@/api/orderProducts'
import { isSavingAtom } from '@/atoms/_editorAtoms'
import {
  useEditorSaveVersion,
  type EditorSaveVersionAnswer,
} from '@/hooks/editor/useEditorSaveVersion'
import {
  releaseEditorSaveLatch,
  takeEditorSaveLatch,
} from '@/utils/editor/editorSaveSession'
import { logger, EventCategory } from '@/utils/logger'

/**
 * Every gesture that writes the line — the header's Save, either layout
 * drawer's save, the text-box template swap, a text-box conversion — runs
 * through here, so the rule that one gesture is one Save lives in one place:
 * take the latch, hold the saving state the editor renders from, take the
 * version this write is covered by (under the gesture's own reason when it
 * names one), and free both however the write ends. A write that gets no
 * version is told why, so a gesture whose only way back is that version can
 * refuse to write.
 *
 * A gesture that could not take the latch is not run at all and answers
 * nothing: another Save is writing the line, and the caller says so its own
 * way — the drawers by their disabled buttons, the swap by refusing.
 */
export const useEditorSaveGesture = () => {
  const setIsSaving = useSetRecoilState(isSavingAtom)
  const takeEditorSaveVersion = useEditorSaveVersion()

  return useCallback(
    async <T>(
      gesture: string,
      applicationType: ApplicationType,
      write: (coveringVersion: EditorSaveVersionAnswer) => Promise<T>,
      versionReason?: EditorSaveVersionReason,
    ): Promise<T | undefined> => {
      const token = takeEditorSaveLatch()
      if (token === null) {
        logger.info({
          message: 'editor_save_skipped_already_saving',
          category: EventCategory.SAVE,
          data: { gesture },
        })
        return undefined
      }
      setIsSaving(true)
      try {
        // The version comes before the write, and before the canvas flush a
        // write may start with: what it holds is the line as the server has
        // it, and taking it first keeps the flush as close to the writes as
        // the round trip allows.
        return await write(
          await takeEditorSaveVersion(applicationType, versionReason),
        )
      } finally {
        // A gesture the editor re-initialised under no longer holds the
        // latch: the re-init cleared the saving state with it, and a Save
        // started since owns both. Only the gesture that still holds the
        // latch clears the state the editor renders from.
        if (releaseEditorSaveLatch(token)) setIsSaving(false)
      }
    },
    [setIsSaving, takeEditorSaveVersion],
  )
}

export default useEditorSaveGesture
